import { useState } from 'react';
import { useMutation } from '@apollo/client';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';

import { CREATE_AFIB, CREATE_NOISE, DELETE_AFIB, DELETE_NOISE, EDIT_INTERVAL } from '../mutations';
import { RECORDING_RPEAKS } from '../queries';
import {
  IntervalEditFeedback,
  IntervalEditOp,
  interpretIntervalEdit,
} from '../utilities/intervalEditFeedback';

type Span = { startIndex: number; endIndex: number };

/**
 * The recording chart's interval edits (noise and AFib) with their outcome
 * made visible (project_management#569): every mutation's error — and every
 * `{success:false}` / not-found payload — ends up in one snackbar instead of
 * an unhandled promise, and the rpeaks query (noises/afibs) is refetched so
 * the chart shows the result.
 *
 * Automatic (analysis) noise is never deleted: "Reject" calls `deleteNoise`,
 * which the backend records as `isValid: false` (kept by reanalysis), and
 * "Restore" is `editInterval` with `isValid: true`.
 */
export function useIntervalEdits(recordingId: string) {
  const [feedback, setFeedback] = useState<IntervalEditFeedback | null>(null);
  const options = {
    refetchQueries: [{ query: RECORDING_RPEAKS, variables: { getMyRecordingId: recordingId } }],
  };
  const [createNoise] = useMutation(CREATE_NOISE, options);
  const [createAFib] = useMutation(CREATE_AFIB, options);
  const [deleteNoise] = useMutation(DELETE_NOISE, options);
  const [deleteAFib] = useMutation(DELETE_AFIB, options);
  const [editInterval] = useMutation(EDIT_INTERVAL, options);

  const run = async (op: IntervalEditOp, mutate: () => Promise<{ data?: unknown }>) => {
    let next: IntervalEditFeedback | null;

    try {
      const result = await mutate();

      next = interpretIntervalEdit(op, { data: result.data as never });
    } catch (error) {
      next = interpretIntervalEdit(op, { error: error as { message?: string } });
    }

    if (next) setFeedback(next);
  };

  return {
    createNoise: ({ startIndex, endIndex }: Span) =>
      run('createNoise', () =>
        createNoise({ variables: { createNoiseInput: { recordingId, startIndex, endIndex } } }),
      ),
    createAFib: ({ startIndex, endIndex }: Span) =>
      run('createAFib', () =>
        createAFib({ variables: { createAFibInput: { recordingId, startIndex, endIndex } } }),
      ),
    deleteNoise: (id: string) =>
      run('deleteNoise', () => deleteNoise({ variables: { deleteNoiseId: id } })),
    deleteAFib: (id: string) =>
      run('deleteAFib', () => deleteAFib({ variables: { deleteAFibId: id } })),
    rejectNoise: (id: string) =>
      run('rejectNoise', () => deleteNoise({ variables: { deleteNoiseId: id } })),
    restoreNoise: ({ id, startIndex, endIndex }: Span & { id: string }) =>
      run('restoreNoise', () =>
        editInterval({ variables: { input: { annotationId: id, startIndex, endIndex, isValid: true } } }),
      ),
    editInterval: ({ id, startIndex, endIndex }: Span & { id: string }) =>
      run('editInterval', () =>
        editInterval({ variables: { input: { annotationId: id, startIndex, endIndex } } }),
      ),
    feedback: (
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={feedback?.severity === 'error' ? null : 6000}
        open={Boolean(feedback)}
        onClose={() => setFeedback(null)}
      >
        <Alert severity={feedback?.severity ?? 'success'} onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    ),
  };
}
